import { Link } from '@remix-run/react'
import { ErrorList } from '#app/components/forms.tsx'
import { InformationBox } from '#app/components/info-box.tsx'
import { SecondsProgress } from '#app/components/seconds-progress.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { useTimeCounter } from '#app/hooks/time.ts'
import { useGetUpscaleImage } from '#app/hooks/use-get-upscale-image.ts'
import { CreditCost } from '#app/services/costs.ts'
import { downloadImageAutodetectingFilename } from '#app/utils/download.ts'
import { cn } from '#app/utils/misc.tsx'

export function ResultImage(props: {
	src?: string
	loading?: boolean
	dontAllowUpscale?: boolean
}) {
	const width = 'w-auto'
	const size = 'h-auto min-h-[30rem] max-h-[40rem] ' + width
	const seconds = useTimeCounter(!props.src && props.loading)

	let { upscale, fetcher } = useGetUpscaleImage()

	const failed = fetcher.data && fetcher.data.prediction == null
	const noCredits = fetcher.data?.error === 'error_credits'

	function getUpscaleBox() {
		const isUpscaling =
			['submitting', 'loading'].includes(fetcher.state) ||
			(!!upscale && !upscale.url)

		if (noCredits) {
			return (
				<div className="flex flex-col gap-2">
					<ErrorList
						errors={["You don't have enough credits to upscale this image"]}
					/>
					<Button asChild className="bg-app-gradient" variant="outline">
						<Link to="/plans">Buy more credits</Link>
					</Button>
				</div>
			)
		} else if (isUpscaling) {
			return (
				<div>
					<SecondsProgress maxSeconds={20} className="px-1">
						<span className="text-body-xs opacity-60">Upscaling image...</span>
					</SecondsProgress>
					<span className="text-sm opacity-60">
						If more than 20 seconds pass, check the "Previous Images" link in
						the top, it should be there
					</span>
				</div>
			)
		} else if (upscale?.url) {
			return (
				<div className="flex w-full flex-row gap-2">
					<Button
						type="button"
						className="flex-[1]"
						onClick={() => downloadImageAutodetectingFilename(upscale.url)}
					>
						<Icon name="download">Download upscale</Icon>
					</Button>
					<Button asChild type="button" className="flex-[1]">
						<a href={upscale.url} target="_blank" rel="noreferrer">
							See upscale
						</a>
					</Button>
				</div>
			)
		} else if (!props.dontAllowUpscale) {
			return (
				<div>
					{failed && (
						<ErrorList
							errors={[
								'There was an error trying to upscale, please try again',
							]}
						/>
					)}
					<Button disabled={!props.src} className="w-full">
						<Icon name="resolution" size="lg">
							Increase resolution ({CreditCost.Upscale} credits){' '}
						</Icon>
						<span className="text-xs opacity-60">&nbsp;~20s</span>
					</Button>
				</div>
			)
		}
	}

	return (
		<div id="result-image">
			<Label>Resulting image</Label>
			{props.src ? (
				<div className="relative">
					<img
						src={props.src}
						alt="Result"
						className={cn('mx-auto rounded-lg object-cover', size)}
					/>
				</div>
			) : (
				<>
					<div
						className={cn(
							'relative flex items-center justify-center rounded-lg border border-muted-foreground text-4xl text-muted-foreground',
							'h-[30rem] w-auto',
							'bg-cover',
						)}
					>
						{seconds > 9 && (
							<div className="righ-1 absolute top-1 w-full py-2 text-sm">
								<InformationBox className="bg-background">
									<div className="flex flex-row justify-center">
										<Icon name="update" className="mr-2 animate-spin">
											Looks like this is taking more time than expected
										</Icon>
									</div>
								</InformationBox>
							</div>
						)}
						<div className="absolute left-0 top-0 z-0 h-full w-full">
							<Icon name="image" className="h-full w-full opacity-5" />
						</div>
						{props.loading ? (
							<div className="z-1 w-full">
								<SecondsProgress />
							</div>
						) : null}
					</div>
				</>
			)}
			<Spacer size="4xs" />
			<fetcher.Form method="POST" action="/me/upscale">
				<input type="hidden" name="url" value={props.src} />
				{getUpscaleBox()}
			</fetcher.Form>
			<Spacer size="4xs" />
			<Button
				disabled={!props.src}
				className="w-full"
				onClick={() => downloadImageAutodetectingFilename(props.src as string)}
			>
				<Icon name="download">Download</Icon>
			</Button>
		</div>
	)
}
